import { ButtonBase } from '@mui/material';
import FeedbackRenderer from 'components/Feedback/FeedbackRenderer';
import Header from 'components/Feedback/Header';
import FeedbackRegenerating from 'components/Grading/FeedbackRegenerating';
import FileViewer from 'components/Grading/FileViewer';
import { PageContext } from 'pages/StudentViewAssignmentFeedback/PageProvider';
import { useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Container, Icon, PDFViewer, RichTextArea, SheetViewer, Stack, Text, styled } from 'shell/components';
import { getS3FileURL } from 'utils/helper';

const Desktop = ({
    dispatch,
    currentFeedback,
    highlights,
    isGenerating,
    isDocumentFullScreen,
    setIsDocumentFullScreen,
}) => {
    const params = useParams();

    const { selectedFeedbackId, setSelectedFeedbackId, onClickThumbsUp, onClickThumbsDown } = useContext(PageContext);

    useEffect(() => {
        dispatch({ type: 'SET_SELECTED_FEEDBACK_ID_TO_FIRST', payload: params.feedbackId });
    }, [dispatch, params.feedbackId]);

    return (
        <CustomContainer>
            <Header />
            <Stack sx={isDocumentFullScreen ? { overflow: 'hidden' } : undefined} direction={'row'}>
                <LeftStack isDocumentFullScreen={isDocumentFullScreen}>
                    {isDocumentFullScreen && (
                        <BannerBox>
                            <Text color={'inherit'}>Currently viewing annotations for feedback.</Text>
                        </BannerBox>
                    )}
                    {typeof currentFeedback.submission === 'string' ? (
                        <Stack sx={{ height: '100%', p: 3 }}>
                            <CustomRichTextArea readOnly value={currentFeedback.submission} onChange={() => {}} />
                        </Stack>
                    ) : currentFeedback.submission.type === 'application/pdf' ? (
                        <PDFViewer
                            highlights={highlights}
                            isFullScreen={isDocumentFullScreen}
                            url={getS3FileURL(currentFeedback.submission)}
                        />
                    ) : [
                          'application/vnd.ms-excel',
                          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                          'text/csv',
                      ].includes(currentFeedback.submission.type) ? (
                        <SheetViewer highlights={highlights} url={getS3FileURL(currentFeedback.submission)} />
                    ) : (
                        <FileViewer
                            device={'desktop'}
                            docUrl={getS3FileURL(currentFeedback.submission)}
                            fileType={currentFeedback.submission.type}
                        />
                    )}
                </LeftStack>
                <CenterButton onClick={() => setIsDocumentFullScreen((current) => !current)}>
                    <Stack alignItems={'center'} spacing={4}>
                        <ArrowIcon kind={isDocumentFullScreen ? 'arrowLeft' : 'arrowRight'} />
                        <CustomText>{isDocumentFullScreen ? 'Show' : 'Hide'}</CustomText>
                    </Stack>
                </CenterButton>
                <RightStack isDocumentFullScreen={isDocumentFullScreen}>
                    {isGenerating ? (
                        <FeedbackRegenerating />
                    ) : (
                        <Stack sx={{ flex: 1 }}>
                            <FeedbackRenderer
                                feedback={currentFeedback}
                                selectedFeedbackId={selectedFeedbackId}
                                setSelectedFeedbackId={setSelectedFeedbackId}
                                onClickThumbsUp={onClickThumbsUp}
                                onClickThumbsDown={onClickThumbsDown}
                            />
                        </Stack>
                    )}
                </RightStack>
            </Stack>
        </CustomContainer>
    );
};

const CustomContainer = styled(Container)(({ theme }) => {
    return {
        position: 'absolute',
        top: 0,
        zIndex: theme.zIndex.drawer + 1,
        minHeight: '100%',
        padding: 0,
        paddingTop: '65px',
        background: theme.palette.ensightful.neutral[100],
    };
});

const LeftStack = styled(Stack)(({ isDocumentFullScreen }) => {
    return {
        position: 'relative',
        width: isDocumentFullScreen ? 'calc(100% - 24px)' : 'calc(60% - 12px)',
        height: 'calc(100vh - 65px)',
        transition: 'width 150ms',
    };
});

const BannerBox = styled(Box)(({ theme }) => {
    return {
        position: 'absolute',
        zIndex: 3,
        width: '100%',
        padding: theme.spacing(1, 5),
        background: theme.palette.ensightful.warning[200],
        color: theme.palette.ensightful.warning[900],
    };
});

const CustomRichTextArea = styled(RichTextArea)(() => {
    return {
        height: '100%',
        '& > .MuiRichTextArea-root': {
            minHeight: '100%',
            maxHeight: '100%',
        },
    };
});

const CenterButton = styled(ButtonBase)(({ theme }) => {
    return {
        width: '24px',
        background: theme.palette.ensightful.neutral[700],
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[100]} solid 3px`,
            outlineOffset: '-5px',
        },
        '&:hover': {
            background: theme.palette.ensightful.neutral[800],
        },
        '&:active': {
            background: theme.palette.ensightful.neutral[900],
        },
    };
});

const ArrowIcon = styled(Icon)(({ theme }) => {
    return {
        color: theme.palette.ensightful.shades[0],
    };
});

const CustomText = styled(Text)(({ theme }) => {
    return {
        width: 'max-content',
        color: theme.palette.ensightful.shades[0],
        transform: 'rotate(-90deg)',
    };
});

const RightStack = styled(Stack)(({ theme, isDocumentFullScreen }) => {
    return {
        width: isDocumentFullScreen ? 0 : 'calc(40% - 12px)',
        height: 'calc(100vh - 65px)',
        background: theme.palette.ensightful.shades[0],
        overflow: 'auto',
        transition: 'width 150ms',
    };
});

export default Desktop;
